import './host'

import { WorkspaceService } from '@graft/nuget-gc.webportals.workspaces'

import { getSessionToken } from '@common/core/helpers'

import { workspaceToObject } from './objects/workspace.object'
import type { Workspace } from './types'

/**
 * Get current workspace for authenticated user.
 * Prefers lastUsedWorkspaceId when provided; otherwise falls back to the first workspace.
 */
export async function getCurrentWorkspace(lastUsedWorkspaceId?: string | null): Promise<Workspace | null> {
  try {
    const token = await getSessionToken()
    const workspaces = await WorkspaceService.getWorkspacesByUserId(token)

    if (!workspaces?.length) {
      return null
    }

    const mapped = await Promise.all(workspaces.map((workspace) => workspaceToObject(workspace)))
    if (lastUsedWorkspaceId) {
      const lastUsed = mapped.find((workspace) => workspace.id === lastUsedWorkspaceId)
      if (lastUsed) {
        return lastUsed
      }
    }

    return mapped[0] ?? null
  } catch (error) {
    console.error('Failed to get current workspace:', error)
    return null
  }
}

/**
 * Get all workspaces for authenticated user
 */
export async function getWorkspacesByUserId(): Promise<Workspace[]> {
  try {
    const token = await getSessionToken()
    const workspaces = await WorkspaceService.getWorkspacesByUserId(token)

    if (workspaces?.length > 0) {
      return await Promise.all(workspaces.map((w) => workspaceToObject(w)))
    }
    return []
  } catch (error) {
    console.error('Failed to get workspaces by user id:', error)
    return []
  }
}

/**
 * Update the last used project for a workspace
 */
export async function updateLastUsedProject(workspaceId: string, lastUsedProjectId: string): Promise<void> {
  try {
    const token = await getSessionToken()
    await WorkspaceService.updateLastUsedProject(token, workspaceId, lastUsedProjectId)
  } catch (error) {
    console.error('Failed to update last used project:', error)
    throw error
  }
}

export async function acceptInvitation(invitationId: string): Promise<void> {
  const { acceptInvitationAndRefreshStores } = await import('@common/modules/memberships/stores/memberships.actions')
  await acceptInvitationAndRefreshStores(invitationId)
}

/**
 * Create a new workspace
 */
export async function createWorkspace(workspaceName: string): Promise<string | null> {
  try {
    const token = await getSessionToken()
    const createdWorkspaceKey = await WorkspaceService.addWorkspace(token, workspaceName)

    return createdWorkspaceKey ?? null
  } catch (error) {
    console.error('Failed to create workspace:', error)
    throw error
  }
}

/**
 * Delete a workspace by id
 */
export async function deleteWorkspace(workspaceId: string): Promise<void> {
  try {
    const token = await getSessionToken()
    await WorkspaceService.deleteWorkspace(token, workspaceId)
  } catch (error) {
    console.error('Failed to delete workspace:', error)
    throw error
  }
}

/**
 * Update the name of a workspace
 */
export async function updateWorkspaceName(workspaceId: string, name: string): Promise<void> {
  try {
    const token = await getSessionToken()
    await WorkspaceService.updateWorkspaceName(token, workspaceId, name)
  } catch (error) {
    console.error('Failed to update workspace name:', error)
    throw error
  }
}

/**
 * Update the logo of a workspace
 */
export async function updateWorkspaceLogo(workspaceId: string, logo: number[]): Promise<void> {
  try {
    const token = await getSessionToken()
    await WorkspaceService.updateWorkspaceLogo(token, workspaceId, logo)
  } catch (error) {
    console.error('Failed to update workspace logo:', error)
    throw error
  }
}
